import React, { FC } from "react";
import Icon from "../Icon/Icon";
import { IconSizeE } from "../../models/enums";
import { useTranslate } from "../../utils/hooks/useTranslate";

import styles from "./reDeliveryIndicator.module.scss";

type ReDeliveryIndicatorPropTypes = {
  /** True when this delivery/campaign was created by reusing ("restoring") a past one. */
  isReDelivery?: boolean;
  /** Source delivery number (delivery surfaces) or source campaign number (campaign surfaces). Shown in
   *  the tooltip and, when showNumber is set, as a visible label next to the icon. */
  sourceNumber?: number | null;
  /** Absolute path to the original delivery/campaign page; opened in a new tab. */
  href?: string | null;
  /** Controls the tooltip wording (delivery vs campaign). */
  type?: "delivery" | "campaign";
  /** When true, render the source number next to the icon (used on the detail pages). */
  showNumber?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Visual indicator that a delivery/campaign was created by reusing ("restoring") a past one. Uses the
 * reuse (reload) icon, a tooltip explaining the origin including the source number, and links to the
 * original delivery/campaign page in a new tab. Renders nothing when the item is not a re-delivery or the
 * source could not be resolved.
 */
const ReDeliveryIndicator: FC<ReDeliveryIndicatorPropTypes> = ({
  isReDelivery,
  sourceNumber,
  href,
  type = "delivery",
  showNumber = false,
  className = "",
  style,
}) => {
  const { t } = useTranslate();

  if (!isReDelivery || !href || !sourceNumber) return null;

  const base =
    type === "campaign"
      ? t`This campaign was created based on a past campaign`
      : t`This delivery was created based on a past delivery`;
  const tooltip = `${base} #${sourceNumber}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      // these icons live inside clickable rows/links — don't trigger the row navigation
      onClick={(e) => e.stopPropagation()}
      className={`${styles.indicator} ${className}`}
      style={style}
    >
      <Icon name="reload" title={tooltip} withoutTranslation size={IconSizeE.S} />
      {showNumber && <span className={styles.number}>#{sourceNumber}</span>}
    </a>
  );
};

export default ReDeliveryIndicator;
